import { Button, SideNav } from "@pej/kore/components";
import type { SideNavGroup } from "@pej/kore/types";
import { Link, useRouterState } from "@tanstack/react-router";
import {
    ClipboardList,
    LayoutDashboard,
    LayoutTemplate,
    PanelLeftClose,
    PanelLeftOpen,
    ScrollText,
    ShieldCheck,
} from "lucide-react";
import { useLayoutEffect, useState } from "react";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { WorkspacePicker } from "./workspace-picker";

const COLLAPSED_KEY = "surveys.sidebar.collapsed";

function readCollapsed(): boolean {
    try {
        return localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
        return false;
    }
}

export function Sidebar() {
    const { workspaceId } = useWorkspaceContext();
    // Remembered per browser (redesign spec §5): collapsing it widens the page
    // area, which is what opens the survey page's preview column.
    const [collapsed, setCollapsed] = useState(readCollapsed);
    // Page chrome that lives outside the sidebar (the create page's save bar)
    // starts at its right edge; kore's rail is w-64 open, w-14 collapsed.
    useLayoutEffect(() => {
        document.documentElement.style.setProperty("--sidebar-w", collapsed ? "3.5rem" : "16rem");
    }, [collapsed]);
    function changeCollapsed(next: boolean) {
        setCollapsed(next);
        try {
            localStorage.setItem(COLLAPSED_KEY, String(next));
        } catch {
            // Storage refused (private mode): the choice lasts this page only.
        }
    }

    // kore never reads the URL itself — it styles whatever `active` the
    // consumer computes. A descendant route (e.g. a future survey detail page)
    // keeps the Surveys row highlighted via the startsWith branch.
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

    const overviewHref = `/w/${workspaceId}`;
    const surveysHref = `/w/${workspaceId}/surveys`;
    const templatesHref = `/w/${workspaceId}/templates`;
    const securityHref = `/w/${workspaceId}/security`;
    const auditHref = `/w/${workspaceId}/audit`;

    const groups: SideNavGroup[] = [
        {
            items: [
                {
                    key: "overview",
                    label: "Overview",
                    icon: <LayoutDashboard className="size-4" />,
                    href: overviewHref,
                    // Exact match only: every other section's path starts
                    // with the workspace root, so the startsWith idiom would
                    // keep Overview lit on all of them.
                    active: pathname === overviewHref,
                },
                {
                    key: "surveys",
                    label: "Surveys",
                    icon: <ClipboardList className="size-4" />,
                    href: surveysHref,
                    active: isActive(surveysHref),
                },
                {
                    key: "templates",
                    label: "Templates",
                    icon: <LayoutTemplate className="size-4" />,
                    href: templatesHref,
                    active: isActive(templatesHref),
                },
                {
                    key: "security",
                    label: "Security",
                    icon: <ShieldCheck className="size-4" />,
                    href: securityHref,
                    active: isActive(securityHref),
                },
                {
                    key: "audit",
                    label: "Audit log",
                    icon: <ScrollText className="size-4" />,
                    href: auditHref,
                    active: isActive(auditHref),
                },
            ],
        },
    ];

    return (
        <SideNav
            ariaLabel="Primary navigation"
            linkComponent={Link}
            groups={groups}
            collapsible
            collapsed={collapsed}
            onCollapsedChange={changeCollapsed}
            header={({ collapsed: isCollapsed, toggle }) => (
                <div className={isCollapsed ? "flex justify-center" : "flex items-center gap-2"}>
                    {!isCollapsed && (
                        <div className="min-w-0 flex-1">
                            <WorkspacePicker />
                        </div>
                    )}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        onClick={toggle}
                    >
                        {isCollapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
                    </Button>
                </div>
            )}
        />
    );
}
